'use client';

import { useCallback, forwardRef, useMemo, type CSSProperties, type HTMLAttributes } from 'react';
import { VirtuosoGrid, VirtuosoGridProps } from 'react-virtuoso';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';
import { MediaGridItem } from './MediaGridItem';
import { Box, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

export default function MediaGridV2() {
    const { state, setSelectedId, toggleSelection, isSelected, deleteMedia, uploadPendingFile, removePendingUpload } = useMediaBrowserV2();

    // Combine pending uploads and items
    const allMedia = useMemo(() => {
        return [...state.pendingUploads, ...state.items];
    }, [state.pendingUploads, state.items]);

    const handleSelect = useCallback((id: string) => {
        // In multi-select mode, use toggle, otherwise set single selection
        if (state.multiSelectMode) {
            toggleSelection(id);
        } else {
            setSelectedId(id);
        }
    }, [state.multiSelectMode, setSelectedId, toggleSelection]);

    const handleDelete = useCallback((media: any) => {
        if ('documentId' in media) {
            // Uploaded file
            deleteMedia(media);
        } else {
            // Pending upload
            removePendingUpload(media.id);
        }
    }, [deleteMedia, removePendingUpload]);

    const handleUpload = useCallback((id: string) => {
        uploadPendingFile(id);
    }, [uploadPendingFile]);

    const renderItem = useCallback((index: number) => {
        const media = allMedia[index];
        const id = 'documentId' in media ? media.documentId : media.id;
        const selected = isSelected(id);

        return (
            <MediaGridItem
                media={media}
                isSelected={selected}
                onSelect={handleSelect}
                onDelete={handleDelete}
                onUpload={handleUpload}
            />
        );
    }, [allMedia, isSelected, handleSelect, handleDelete, handleUpload]);

    const computeItemKey = useCallback((index: number) => {
        const media = allMedia[index];
        return 'documentId' in media ? media.documentId : media.id;
    }, [allMedia]);

    if (state.isLoading && allMedia.length === 0) {
        return (
            <Box className="flex h-full w-full items-center justify-center">
                <Typography variant="body1" color="text.secondary">
                    Medien werden geladen...
                </Typography>
            </Box>
        );
    }

    if (!state.isLoading && allMedia.length === 0) {
        return (
            <Box className="flex h-full w-full flex-col items-center justify-center gap-4">
                <CloudUpload sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography variant="h6" color="text.secondary">
                    Keine Medien gefunden
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Lade Medien hoch oder ändere deine Suchfilter
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="h-full w-full">
            <VirtuosoGrid
                style={{ height: '100%', width: '100%' }}
                totalCount={allMedia.length}
                components={gridComponents}
                computeItemKey={computeItemKey}
                itemContent={renderItem}
                overscan={100}
                scrollSeekConfiguration={{
                    enter: (velocity) => Math.abs(velocity) > 300,
                    exit: (velocity) => Math.abs(velocity) < 50,
                }}
                useWindowScroll={false}
                increaseViewportBy={{ top: 200, bottom: 200 }}
            />
        </Box>
    );
}

// Grid components - defined outside to prevent re-mounting
type GridListProps = HTMLAttributes<HTMLDivElement> & {
    style?: CSSProperties;
};

const GridList = forwardRef<HTMLDivElement, GridListProps>(function GridList(
    { style, children, className, ...props },
    ref,
) {
    return (
        <div
            ref={ref}
            {...props}
            className={`box-border grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4 px-2 ${className ?? ''}`}
            style={style}
        >
            {children}
        </div>
    );
});

const gridComponents: VirtuosoGridProps<undefined, undefined>['components'] = {
    Header: () => <div className="h-3" />,
    Footer: () => <div className="h-3" />,
    List: GridList,
    Item: ({ children, ...props }) => (
        <div {...props} className="flex flex-col">
            {children}
        </div>
    ),
};
